import { useEffect, useState, useRef, useCallback } from "react";
import { StoreRankBadge } from "@/components/StoreRankBadge";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, QrCode, Sparkles, X, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import GameEngine from "@/components/GameEngine";
import PrizeJackpot from "@/components/PrizeJackpot";
import ContestMedia from "@/components/ContestMedia";
import { ClosedBanner } from "@/components/ClosedBanner";
import { RulesButton, RulesQr } from "@/components/ContestRules";
import { RulesAcceptGate, contestHasRules, rulesAcceptedLocally, rememberRulesAccepted } from "@/components/RulesAcceptGate";
import WinReceipt from "@/components/WinReceipt";
import RegistrationGate from "@/components/RegistrationGate";
import { useCustomer } from "@/lib/customer";
import Logo from "@/components/Logo";
import { fileUrl } from "@/lib/upload";
import ScreenMedia, { useViewportFormat } from "@/components/ScreenMedia";
import { themeClass } from "@/lib/theme";
import { KioskReceiptButton } from "@/components/KioskReceiptButton";
import { useGameSounds } from "@/lib/sounds";
import { WrongStoreScreen } from "@/components/WrongStoreScreen";

const FADE_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const POP_IN = { initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 } };

const hexA = (hex, a) => {
  const h = (hex || "#2563eb").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

export default function KioskGame() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const widget = useLocation().pathname.startsWith("/widget");
  const partnerKey = params.get("key") || "";
  const web = useLocation().pathname.startsWith("/online") || widget;
  const embed = useLocation().pathname.startsWith("/embed");
  const storeId = web ? null : params.get("sede") || null;
  const [contest, setContest] = useState(null);
  const vpFormat = useViewportFormat(params.get("format"), contest?.game?.custom_formats);
  const [code, setCode] = useState("");
  const [gateEmail, setGateEmail] = useState(null);
  const { customer, refresh: refreshCustomer } = useCustomer();
  const preview = params.get("preview") === "1";
  const [rulesOk, setRulesOk] = useState(false);
  const rulesVersion = contest?.rules_version || 1;
  const customerAccepted = !!customer?.rules_accepted?.[id] && (customer?.rules_accepted_version?.[id] || 1) === rulesVersion;
  const rulesAccepted = rulesOk || customerAccepted || (!customer && !!contest && rulesAcceptedLocally(id, rulesVersion));
  const rulesUpdated = !!contest && rulesVersion > 1 && (customer ? !!customer?.rules_accepted?.[id] : !!localStorage.getItem(`vw_rules_ok_${id}`)) && !rulesAccepted;
  const needsRulesGate = contestHasRules(contest) && !preview && !rulesAccepted;
  const acceptRules = () => { setRulesOk(true); if (!customer) rememberRulesAccepted(id, rulesVersion); };
  const [phase, setPhase] = useState("input"); // input | playing | win | lose
  const [play, setPlay] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wrongStore, setWrongStore] = useState(null);
  const scannerRef = useRef(null);
  const [previewGame, setPreviewGame] = useState(null);

  useEffect(() => {
    if (widget && partnerKey) {
      api.partnerWidgetConfig(id, partnerKey).then((d) => {
        const g = { ...(d.contest.game || {}) };
        if (d.partner?.brand_color) g.accent_color = d.partner.brand_color;
        setContest({ ...d.contest, game: g, partner_logo: d.partner?.logo_url || null });
      }).catch((e) => toast.error(e?.response?.data?.detail?.message || "Chiave partner non valida o concorso non abilitato"));
      return;
    }
    api.contestPublic(id, storeId).then(setContest).catch(() => toast.error("Concorso non trovato"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- carica il concorso una volta per id; i setter sono stabili

  useEffect(() => {
    if (!preview) return;
    const on = (e) => {
      const d = e.data || {};
      if (d.type !== "viviwin-preview") return;
      if (d.game) setPreviewGame(d.game);
      if (d.phase) {
        if (d.phase === "win") setPlay({ id: "demo", result: "win", prize_name: "Premio di esempio", prize_value: "50€", receipt_serial: "WIN-DEMO-000000", created_at: new Date().toISOString(), contest_name: "" });
        if (d.phase === "playing" || d.phase === "lose") setPlay({ id: "demo", result: d.phase === "playing" ? "win" : "lose", prize_name: "Premio di esempio", receipt_serial: "WIN-DEMO", created_at: new Date().toISOString() });
        setPhase(d.phase === "idle" ? "input" : d.phase);
      }
    };
    window.addEventListener("message", on);
    window.parent?.postMessage({ type: "viviwin-preview-ready" }, window.location.origin);
    return () => window.removeEventListener("message", on);
  }, [preview]); // eslint-disable-line react-hooks/exhaustive-deps -- solo in modalità anteprima

  const effGame = previewGame || contest?.game || {};
  const sounds = useGameSounds(effGame);
  useEffect(() => { sounds.setDucked(phase !== "input"); }, [phase, sounds]);

  const startPlay = async () => {
    if (!code.trim()) return toast.error("Inserisci un codice o scansiona il QR");
    setBusy(true);
    try {
      const result = widget
        ? await api.partnerPlay(partnerKey, { contest_id: id, code: code.trim(), external_user_id: params.get("user") || undefined, email: params.get("email") || undefined, rules_accepted: true })
        : await api.play({ contest_id: id, code: code.trim(), store_id: storeId, customer_email: gateEmail || undefined, rules_accepted: rulesAccepted, ...(web ? { mode: "web" } : {}), ...api.refContext() });
      setPlay(result);
      setPhase("playing");
      sounds.play("spin");
      if (customer && contestHasRules(contest) && !customerAccepted) refreshCustomer?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.outcome === "wrong_store") { setWrongStore(d.store_name); sounds.play("lose"); return; }
      toast.error(typeof d === "string" ? d : d?.message || "Codice non valido");
    } finally {
      setBusy(false);
    }
  };

  const onGameComplete = () => {
    sounds.stop();
    sounds.play(play.result === "win" ? "win" : "lose");
    setPhase(play.result === "win" ? "win" : "lose");
  };

  const reset = () => {
    setCode("");
    setPlay(null);
    setPhase("input");
  };

  const stopScan = useCallback(() => {
    scannerRef.current?.stop().catch(() => {});
    setScanning(false);
  }, []);

  useEffect(() => {
    if (!scanning) return;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    scanner
      .start({ facingMode: "environment" }, { fps: 10, qrbox: 250 }, (decoded) => {
        setCode(decoded);
        stopScan();
        toast.success("Codice QR letto");
      }, () => {})
      .catch(() => toast.error("Impossibile accedere alla fotocamera"));
    return () => {
      scanner.stop().catch(() => {});
    };
  }, [scanning, stopScan]);

  if (!contest) return <div className="min-h-screen bg-slate-950 grid place-items-center text-white/60">Caricamento…</div>;
  if (web && !widget && !contest.web_enabled && !preview)
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center text-center text-white p-8" data-testid="web-not-enabled">
        <div><h1 className="font-display font-bold text-3xl">{contest.name}</h1><p className="mt-3 text-white/70">Questo concorso non è giocabile online: partecipa {contest.channel === "whatsapp" ? "via WhatsApp" : "al totem in negozio"}.</p><div className="mt-4"><RulesButton c={contest} /></div></div>
      </div>
    );
  if (widget && !partnerKey) return <div className="min-h-screen bg-slate-950 grid place-items-center text-white p-8 text-center" data-testid="widget-key-missing"><p>Widget non configurato: manca la chiave partner (?key=…)</p></div>;
  if (web && !widget && customer === null && !preview) {
    const back = encodeURIComponent(window.location.pathname);
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center text-center text-white p-8" data-testid="web-login-required">
        <div className="max-w-sm">
          <h1 className="font-display font-bold text-3xl">{contest.name}</h1>
          <p className="mt-3 text-white/70">Per giocare online devi accedere o registrarti: è gratis e ti serve per ricevere la ricevuta di vincita.</p>
          <Button data-testid="web-login-button" onClick={() => navigate(contest.landing?.slug ? `/c/${contest.landing.slug}/accedi?next=${back}` : `/accedi?next=${back}`)} className="mt-6 h-12 px-8">Accedi o registrati</Button>
        </div>
      </div>
    );
  }
  if (contest.channel === "whatsapp" && !preview && !web)
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center text-center text-white p-8" data-testid="kiosk-whatsapp-only">
        <div>
          <h1 className="font-display font-bold text-3xl">{contest.name}</h1>
          <p className="mt-3 text-white/70">Questo concorso si gioca esclusivamente via WhatsApp: invia il tuo codice al numero indicato sul materiale promozionale.</p>
          <div className="mt-4"><RulesButton c={contest} /></div>
        </div>
      </div>
    );
  const g = effGame;
  const winContest = { ...contest, game: g };
  const logo = fileUrl(contest.logo_file_id) || contest.logo_url;

  return (
    <div className={`min-h-screen text-white relative overflow-hidden ${themeClass(g.bg_color, !!(g.screens && g.screens.length))}`} data-testid="kiosk-themed"
      style={{ background: `radial-gradient(circle at 20% 10%, ${hexA(g.primary_color, 0.55)}, transparent 45%), radial-gradient(circle at 85% 90%, ${hexA(g.accent_color, 0.35)}, transparent 40%), ${g.bg_color || "#ffffff"}` }}>
      <button data-testid="mute-button" onClick={() => sounds.setMuted(!sounds.muted)} style={{ color: "#fff" }} className="fixed top-3 right-3 z-30 h-10 w-10 rounded-full bg-black/40 backdrop-blur grid place-items-center opacity-80 hover:opacity-100" title={sounds.muted ? "Attiva audio" : "Disattiva audio"}>
        {sounds.muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
      <ScreenMedia screens={g.screens} phase={phase === "input" ? "idle" : phase} format={vpFormat} fit={g.screens_fit || "cover"} className="fixed">
        <div className="absolute inset-0 bg-black/45" />
      </ScreenMedia>
      <div className="max-w-2xl mx-auto px-6 py-8 relative">
        {!embed && !widget && (
        <button
          data-testid="game-back-button"
          onClick={() => navigate(web ? (contest.landing?.slug ? `/c/${contest.landing.slug}` : "/concorsi") : "/gioca")}
          className={`flex items-center gap-2 transition-colors mb-6 ${web ? "rounded-full bg-black/60 backdrop-blur px-4 py-2 text-sm font-semibold hover:bg-black/75" : "text-white/60 hover:text-white"}`}
          style={web ? { color: "#fff" } : undefined}
        >
          <ArrowLeft className="h-4 w-4" /> {web ? "Torna al concorso" : "Cambia concorso"}
        </button>
        )}

        <div className="text-center mb-8">
          <div className="inline-block rounded-2xl bg-white p-2 shadow-xl mb-4">
            {logo ? <img src={logo} alt={contest.name} className="h-16 w-auto object-contain" data-testid="contest-logo" /> : <Logo className="h-16" />}
          </div>
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold mb-3 sm:ml-3 align-top border" style={{ background: hexA(g.accent_color, 0.15), borderColor: hexA(g.accent_color, 0.4), color: g.accent_color }}>
            <Sparkles className="h-4 w-4" /> {contest.name}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="welcome-title">
            {g.welcome || "Instant Win"}
          </h1>
          {contest.description && <p className="text-white/70 mt-2 max-w-lg mx-auto">{contest.description}</p>}
          <div className="mt-3"><RulesButton c={contest} /></div>
        </div>

        {phase === "input" && !embed && !web && <StoreRankBadge storeId={storeId} contestId={id} accent={g.accent_color} />}
        {phase === "input" && <ContestMedia contest={contest} />}
        {phase === "input" && contest.opening && !contest.opening.open && <ClosedBanner opening={contest.opening} accent={g.accent_color} />}
        {wrongStore && <WrongStoreScreen storeName={wrongStore} accent={g.accent_color} onClose={() => setWrongStore(null)} />}
        {phase === "input" && !embed && !web && <div className="flex justify-center mb-6"><RulesQr c={contest} light size={96} /></div>}

        {phase === "input" && contest.receipt_ocr_enabled && !preview && (
          <div className="flex justify-center mb-6" data-testid="kiosk-receipt-wrap">
            <KioskReceiptButton contestId={id} storeId={storeId} accent={g.accent_color} onPlayed={(result) => { setPlay(result); setPhase("playing"); sounds.play("spin"); }} />
          </div>
        )}

        {phase === "input" && contest.prizes?.length > 0 && (
          <>
          {contest.prizes_available_today > 0 && <PrizeJackpot count={contest.prizes_available_today} scope={contest.prizes_scope} accent={g.accent_color} />}
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="prize-strip">
            {contest.prizes.slice(0, 6).map((p) => (
              <span key={p.id || p.name} className="rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                🎁 {p.name}{p.value ? ` · ${p.value}` : ""}
              </span>
            ))}
          </div>
          </>
        )}

        {phase === "input" && contest.access_mode === "registered" && !customer && !gateEmail && !preview && (
          <RegistrationGate contest={contest} onIdentified={setGateEmail} />
        )}

        {phase === "input" && needsRulesGate && !(contest.access_mode === "registered" && !customer && !gateEmail) && (
          <RulesAcceptGate contest={contest} onAccept={acceptRules} updated={rulesUpdated} />
        )}

        {phase === "input" && !needsRulesGate && !(contest.access_mode === "registered" && !customer && !gateEmail && !preview) && (
          <motion.div {...FADE_UP} className="rounded-3xl bg-white/95 text-slate-900 p-8 shadow-2xl">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {g.instructions || "Inserisci il tuo codice"}
            </label>
            <Input
              data-testid="play-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ES. MI-ABCD1234 o carta fedeltà"
              className="mt-2 h-14 text-center text-xl font-mono-code tracking-widest bg-slate-100 border-slate-200 text-slate-900"
            />

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Button
                data-testid="scan-qr-button"
                variant="outline"
                onClick={() => setScanning(true)}
                className="h-14 gap-2 border-slate-300 text-slate-700 text-base"
              >
                <QrCode className="h-5 w-5" /> Scansiona QR
              </Button>
              <Button
                data-testid="play-now-button"
                onClick={startPlay}
                disabled={busy}
                className="h-14 gap-2 text-base font-bold text-white hover:opacity-90"
                style={{ background: g.primary_color }}
              >
                <Sparkles className="h-5 w-5" /> {busy ? "Attendere…" : "GIOCA ORA"}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center mt-4">
              Puoi inserire un codice univoco distribuito nei punti vendita oppure scansionare il QR code riportato sulla cartolina/scontrino.
            </p>
          </motion.div>
        )}

        {phase === "playing" && <GameEngine type={g.type} theme={g} result={play.result} onComplete={onGameComplete} prizes={contest.prizes} winPrize={play.prize_name} sounds={sounds} />}

        {phase === "lose" && (
          <motion.div
            {...POP_IN}
            className="rounded-3xl bg-white/95 text-slate-900 p-10 text-center shadow-2xl"
            data-testid="lose-screen"
          >
            <div className="text-6xl mb-4">🙁</div>
            <h2 className="font-display font-black text-3xl">{g.lose_title || "Ritenta!"}</h2>
            <p className="text-slate-600 mt-2">{g.lose_message || "Questa volta non hai vinto. Sarà per la prossima!"}</p>
            <Button data-testid="play-again-button" onClick={reset} className="mt-6 h-12 px-8">
              Nuova giocata
            </Button>
          </motion.div>
        )}

        {phase === "win" && <WinReceipt play={play} contest={winContest} onClose={reset} playSound={sounds.play} web={web} />}
      </div>

      {scanning && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6">
          <div className="bg-white rounded-2xl p-4 max-w-sm w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-slate-900">Inquadra il QR code</h3>
              <button data-testid="close-scan-button" onClick={stopScan} className="text-slate-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div id="qr-reader" className="w-full rounded-xl overflow-hidden" />
          </div>
        </div>
      )}
    </div>
  );
}
